import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DateRangePicker } from '@/components/ui/date-picker';
import { client, formatPrice } from '@vn-propane/shared';

interface OrderStatusBreakdown {
  status: string;
  count: number;
}

interface DriverDeliveries {
  driver_id: string;
  driver_name: string | null;
  delivery_count: number;
  total_revenue: number;
}

interface ProductRevenue {
  product_id: string;
  product_name: string | null;
  revenue: number;
  quantity_sold: number;
}

interface DashboardSummary {
  total_orders: number;
  total_revenue: number;
  completed_deliveries: number;
  avg_delivery_time_minutes: number;
  orders_by_status: OrderStatusBreakdown[];
  top_drivers: DriverDeliveries[];
  top_products: ProductRevenue[];
}

function getDefaultDateRange(): { start: Date; end: Date } {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date();
  start.setDate(start.getDate() - 29);
  start.setHours(0, 0, 0, 0);
  return { start, end };
}

export default function ReportsDashboard() {
  const { id: orgId } = useParams<{ id: string }>();
  const { t } = useTranslation(['common', 'orders']);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<DashboardSummary | null>(null);

  const defaultRange = getDefaultDateRange();
  const [startDate, setStartDate] = useState<Date | undefined>(defaultRange.start);
  const [endDate, setEndDate] = useState<Date | undefined>(defaultRange.end);

  const fetchData = useCallback(async () => {
    if (!orgId || !startDate || !endDate) return;

    setLoading(true);
    setError(null);

    try {
      const response = await client.get({
        url: `/gas_admin/organizations/${orgId}/reports/dashboard`,
        query: {
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
        },
      });

      if (response.error) {
        setError('Failed to load dashboard data');
        return;
      }

      setData((response.data as { data: DashboardSummary } | undefined)?.data ?? null);
    } catch (err) {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [orgId, startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Normalize status from camelCase to snake_case (e.g., "inProgress" -> "in_progress")
  const normalizeStatus = (status: string): string => {
    return status.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '');
  };

  const fallbackLabels: Record<string, string> = {
    pending: 'Pending',
    assigned: 'Assigned',
    in_progress: 'In Progress',
    arrived: 'Arrived',
    delivered: 'Delivered',
    cancelled: 'Cancelled',
  };

  const getStatusLabel = (status: string): string => {
    const normalized = normalizeStatus(status);
    const translated = t(`orders:status.${normalized}`, { defaultValue: '' });
    if (translated && !translated.startsWith('orders.') && !translated.startsWith('orders:')) {
      return translated;
    }
    return fallbackLabels[normalized] || status;
  };

  return (
    <div className="space-y-6">
      {/* Date Range Filter */}
      <Card>
        <CardHeader>
          <CardTitle>{t('reports.dateRange')}</CardTitle>
        </CardHeader>
        <CardContent>
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            startLabel={t('labels.from')}
            endLabel={t('labels.to')}
          />
        </CardContent>
      </Card>

      {loading && (
        <div className="text-center py-8 text-muted-foreground">
          {t('labels.loading')}
        </div>
      )}

      {error && (
        <div className="text-center py-8 text-destructive">
          {error}
        </div>
      )}

      {!loading && !error && data && (
        <>
          {/* Summary Cards */}
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  {t('reports.metrics.totalOrders')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-extrabold tracking-tight">{data.total_orders}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  {t('reports.metrics.totalRevenue')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-extrabold tracking-tight">
                  {formatPrice(String(data.total_revenue), 'VND')}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  {t('reports.metrics.completedDeliveries')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-extrabold tracking-tight">{data.completed_deliveries}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  {t('reports.metrics.avgDeliveryTime')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-extrabold tracking-tight">
                  {Math.round(data.avg_delivery_time_minutes)} {t('reports.minutes')}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Order Status Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>{t('reports.ordersByStatus')}</CardTitle>
            </CardHeader>
            <CardContent>
              {data.orders_by_status.length === 0 ? (
                <p className="text-muted-foreground">{t('reports.noData')}</p>
              ) : (
                <div className="space-y-2">
                  {data.orders_by_status.map((item) => (
                    <div key={item.status} className="flex justify-between items-center">
                      <span className="text-sm">
                        {getStatusLabel(item.status)}
                      </span>
                      <span className="font-medium">{item.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top Drivers and Products */}
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>{t('reports.topDrivers')}</CardTitle>
              </CardHeader>
              <CardContent>
                {data.top_drivers.length === 0 ? (
                  <p className="text-muted-foreground">{t('reports.noData')}</p>
                ) : (
                  <div className="space-y-3">
                    {data.top_drivers.map((driver) => (
                      <div key={driver.driver_id} className="flex justify-between items-center">
                        <div>
                          <div className="font-medium">
                            {driver.driver_name || t('reports.unknownDriver')}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {driver.delivery_count} {t('reports.deliveries')}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-medium">
                            {formatPrice(String(driver.total_revenue), 'VND')}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('reports.topProducts')}</CardTitle>
              </CardHeader>
              <CardContent>
                {data.top_products.length === 0 ? (
                  <p className="text-muted-foreground">{t('reports.noData')}</p>
                ) : (
                  <div className="space-y-3">
                    {data.top_products.map((product) => (
                      <div key={product.product_id} className="flex justify-between items-center">
                        <div>
                          <div className="font-medium">
                            {product.product_name || t('reports.unknownProduct')}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {product.quantity_sold} {t('reports.unitsSold')}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-medium">
                            {formatPrice(String(product.revenue), 'VND')}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
